import React, { useEffect } from "react";
import { RegisterOptions, useFormContext } from "react-hook-form";
import { getNestedError } from "utils/splitDot";

interface RangeSelectorProps {
  id: string;
  name: string;
  value?: number;
  min?: number;
  max?: number;
  step?: number;
  onChange?: (value: number) => void;
  disabled?: boolean;
  readOnly?: boolean;
  className?: string;
  validations?: RegisterOptions;
}

const RangeSelector: React.FC<RangeSelectorProps> = ({
  id,
  name,
  value = 2,
  min = 2,
  max = 99,
  step = 1,
  onChange,
  disabled = false,
  readOnly = true,
  className = "",
  validations,
}) => {
  const {
    register,
    setValue,
    formState: { errors },    
  } = useFormContext();
  // Use watch to keep the field value in sync with React Hook Form
  const { watch, trigger } = useFormContext();
  const currentValue = watch(name) ?? value;
  

  const handleChange = (val: number) => {
    const newVal = Math.min(max, Math.max(min, val));
    setValue(name, newVal, { shouldValidate: true });
    trigger(name); // ensure validation runs
    onChange?.(newVal);
  };

  const increment = () => {
    if (disabled) return;
    handleChange(Number(currentValue) + step);
  };

  const decrement = () => {
    if (disabled) return;
    handleChange(currentValue - step);
  };

  /**
   * Handles input change for the number field.
   * Allows empty input so user can delete all digits.
   */
  /**
   * Handles input change for the number field.
   * Prevents empty input by resetting to min value if input is empty.
   */
  /**
   * Handles input change for the number field.
   * Allows empty input so user can delete all digits.
   */
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const valStr = e.target.value;
    if (valStr === "") {
      setValue(name, "", { shouldValidate: true });
      onChange?.(NaN);
      return;
    }
    const val = parseInt(valStr, 10);
    if (!isNaN(val)) handleChange(val);
  };

  const errorMessage =
    name && getNestedError(errors, name)?.message
      ? getNestedError(errors, name).message
      : undefined;

  useEffect(() => {
    setValue(name, value === undefined ? min : Number(value), { shouldValidate: true });
  }, [value, min, name, setValue]);

  return (
    <>
      <div className={`d-flex align-items-center gap-5px ${className}`}>
        <button
          type="button"
          onClick={decrement}
          disabled={disabled || currentValue <= min}
          className={`btn-theme px-0 min-w-40px text-white font-figtree fs-27px fw-normal ${
            currentValue <= min ? "bg-disabled" : "bg-grey-1 hover:bg-grey-2"
          }`}
        >
          -
        </button>
        <div className="flex-fill">
          <input
            id={id}
            {...register(name, validations)}
            type="number"
            className="input-field text-center w-100"
            value={currentValue}
            min={min}
            max={max}
            onChange={handleInputChange}
            readOnly={readOnly}
          />
        </div>
        <button
          type="button"
          onClick={increment}
          disabled={disabled || currentValue >= max}
          className={`btn-theme px-0 min-w-40px text-white font-figtree fs-30px fw-normal ${
            currentValue >= max ? "bg-disabled" : "bg-grey-1 hover:bg-grey-2"
          }`}
        >
          +
        </button>
      </div>
      {errorMessage && (
        <div className="text-red fs-14px">{errorMessage?.toString()}</div>
      )}
    </>
  );
};

export default RangeSelector;
